import { useCallback, useEffect, useState, type FormEvent } from "react";
import { LoaderCircle, ShieldCheck } from "lucide-react";
import type { Role } from "@cop/domain";
import { api } from "../lib/api";
import { errorMessage } from "../lib/firebase";
import { ROLE_LABEL, useSession } from "../lib/session";

const GRANTABLE: Role[] = ["owner", "branch_admin", "receptionist", "dentist", "specialist", "assistant", "billing", "auditor"];

type Row = Awaited<ReturnType<typeof api.listStaff>>["staff"][number];

/** Superadmin only: grant or revoke team roles. The superadmin account itself is locked. */
export function Admins() {
  const { user } = useSession();
  const [rows, setRows] = useState<Row[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [revoking, setRevoking] = useState<string | null>(null);

  const load = useCallback(() => {
    api
      .listStaff({})
      .then((r) => setRows(r.staff))
      .catch((e) => setError(errorMessage(e)));
  }, []);
  useEffect(load, [load]);

  async function grant(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const f = new FormData(form);
    const email = String(f.get("email") ?? "").trim();
    const role = String(f.get("role")) as Role;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.setStaffRole({ email, role });
      setNotice(`${email} ahora tiene el rol ${ROLE_LABEL[role]}. Lo verá la próxima vez que ingrese.`);
      form.reset();
      load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function revoke(email: string) {
    setBusy(true);
    setError(null);
    try {
      await api.revokeStaffRole({ email });
      setRevoking(null);
      setNotice(`Se quitó el acceso al panel a ${email}.`);
      load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page">
      <header className="toolbar">
        <div className="toolbar-title">
          <h1>Administradores</h1>
          <p className="muted small">Sólo vos podés dar o quitar roles. Tu cuenta no se puede modificar ni quitar desde el panel.</p>
        </div>
      </header>
      {error && <p className="notice notice-danger">{error}</p>}
      {notice && <p className="notice">{notice}</p>}

      <form className="admin-grant" onSubmit={grant}>
        <label className="field">
          <span>Email de la cuenta de Google</span>
          <input name="email" type="email" required placeholder="nombre@gmail.com" autoComplete="off" />
        </label>
        <label className="field">
          <span>Rol</span>
          <select name="role" defaultValue="receptionist">
            {GRANTABLE.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABEL[r]}
              </option>
            ))}
          </select>
        </label>
        <button className="btn btn-primary" disabled={busy}>
          {busy && <LoaderCircle className="spin" size={16} aria-hidden />} Asignar rol
        </button>
        <p className="muted small admin-grant-hint">La persona tiene que haber ingresado al menos una vez con Google.</p>
      </form>

      {rows === null ? (
        <div className="skeleton-rows" aria-busy="true" />
      ) : (
        <ul className="admin-list">
          {rows.map((r) => {
            const locked = r.role === "superadmin";
            const self = r.uid === user?.uid;
            return (
              <li key={r.uid}>
                <div>
                  <strong>{r.name ?? r.email}</strong>
                  <span className="muted small">{r.email}</span>
                </div>
                <span className="admin-role">
                  {locked && <ShieldCheck size={14} aria-hidden />} {ROLE_LABEL[r.role as Role] ?? r.role}
                  {self && <span className="admin-self">Vos</span>}
                </span>
                <div className="admin-actions">
                  {!locked &&
                    !self &&
                    (revoking === r.email ? (
                      <>
                        <button className="btn btn-sm btn-danger" onClick={() => revoke(r.email)} disabled={busy}>
                          Quitar acceso
                        </button>
                        <button className="btn btn-sm" onClick={() => setRevoking(null)} disabled={busy}>
                          Cancelar
                        </button>
                      </>
                    ) : (
                      <button className="btn btn-sm btn-danger-quiet" onClick={() => setRevoking(r.email)}>
                        Quitar
                      </button>
                    ))}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
